import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import HeroFeatures from "./hero/HeroFeatures";

const HERO_IMAGE_BASE =
  "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F195038848874421da49fe9272955c2ac?format=webp&width=";
export const HERO_IMAGE = `${HERO_IMAGE_BASE}1800`;
// Responsive sources so the browser fetches the smallest sufficient size for
// the viewport — keeps the LCP image fast on mobile while staying crisp on 4K.
export const HERO_SRCSET = [640, 1024, 1440, 1800, 2400]
  .map((w) => `${HERO_IMAGE_BASE}${w} ${w}w`)
  .join(", ");

function useTopCities(limit = 6) {
  return useQuery({
    queryKey: ["top-cities", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("city")
        .eq("status", "active")
        .not("city", "is", null);
      if (error) throw error;

      // Count listings per city
      const counts: Record<string, number> = {};
      for (const row of data || []) {
        if (row.city) {
          counts[row.city] = (counts[row.city] || 0) + 1;
        }
      }

      // Sort by count descending and take top N
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([city]) => city);
    },
    staleTime: 60_000,
  });
}

export default function HeroSection() {
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const navigate = useNavigate();
  const { data: topCities } = useTopCities();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const loc = location.trim();
    const term = query.trim();

    // When a location is entered, send the user to the map and zoom into that
    // area so they can see what's in/around it. Treatment-only searches still
    // go to the browse listing page.
    if (loc) {
      const params = new URLSearchParams();
      if (/^\d{5}$/.test(loc)) {
        params.set("zip", loc);
      } else {
        params.set("city", loc);
      }
      if (term) params.set("search", term);
      navigate(`/map?${params}`);
      return;
    }

    const params = new URLSearchParams();
    if (term) params.set("search", term);
    navigate(`/browse${params.toString() ? `?${params}` : ""}`);
  };

  return (
    <section className="relative overflow-hidden border-b border-border">
      {/* Full-bleed hero photo — the artwork is already dark on the left,
          so we only add light overlays to guarantee text contrast. */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          srcSet={HERO_SRCSET}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
      </div>
      <div className="absolute top-24 right-[6%] w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" />

      <div className="relative container mx-auto px-4 lg:px-8 pt-16 pb-20 lg:pt-28 lg:pb-28 min-h-[640px] lg:min-h-[720px] flex items-center">
        <div className="w-full">
          {/* Copy + search */}
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary mb-5 opacity-0 animate-fade-in">
              The Premier Directory for MedSpas
            </p>

            <h1
              className="font-serif-display font-bold tracking-tight text-foreground leading-[0.95] mb-5 opacity-0 animate-fade-in"
              style={{ animationDelay: "100ms" }}
            >
              <span className="block text-4xl sm:text-5xl lg:text-6xl">
                Find Your Perfect
              </span>
              <span className="block text-gradient text-6xl sm:text-7xl lg:text-8xl mt-2 pb-2">
                Med Spa
              </span>
            </h1>

            <p
              className="text-lg text-muted-foreground max-w-lg mb-8 opacity-0 animate-fade-in"
              style={{ animationDelay: "200ms" }}
            >
              Search verified med spas, explore treatments, and connect with top
              providers near you.
            </p>

            {/* Search bar */}
            <form
              onSubmit={handleSearch}
              className="opacity-0 animate-slide-up"
              style={{ animationDelay: "300ms" }}
            >
              <div className="flex flex-col sm:flex-row gap-3 max-w-xl bg-card/90 backdrop-blur rounded-2xl p-2 shadow-xl shadow-black/20 border border-border">
                <div className="flex-1 flex items-center gap-2 px-4">
                  <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                  <input
                    id="hero-search"
                    name="hero-search"
                    type="text"
                    autoComplete="off"
                    placeholder="Business Name..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full py-3 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
                  />
                </div>
                <div className="flex items-center gap-2 px-4 sm:border-l border-border">
                  <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                  <input
                    id="hero-location"
                    name="hero-location"
                    type="text"
                    autoComplete="off"
                    placeholder="City or zip code..."
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full py-3 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity shrink-0"
                >
                  Search
                </button>
              </div>
            </form>

            {/* Popular searches */}
            <div
              className="mt-5 flex flex-wrap items-center gap-2 opacity-0 animate-fade-in"
              style={{ animationDelay: "450ms" }}
            >
              <span className="text-xs text-muted-foreground">Popular:</span>
              {(topCities || []).map((city) => (
                <button
                  key={city}
                  onClick={() =>
                    navigate(`/map?city=${encodeURIComponent(city)}`)
                  }
                  className="text-xs font-medium text-primary hover:underline transition-colors"
                >
                  {city}
                </button>
              ))}
            </div>

            {/* Feature highlights */}
            <div
              className="mt-12 opacity-0 animate-fade-in"
              style={{ animationDelay: "550ms" }}
            >
              <HeroFeatures />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
